import type { FC } from 'react';
import { useTranslation } from 'react-i18next';

import {
  Card,
  CardContent,
  CardHeader,
  CardTitle,
  Empty,
  EmptyDescription,
  EmptyHeader,
  EmptyTitle,
  LoaderOverlay,
} from '@/shared/ui';
import { useDealFunnel, useMyDashboard } from '@entities/Deal';

const FUNNEL_KEYS = [
  'submitted',
  'in_review',
  'accepted',
  'declined',
  'revoked',
  'expired',
  'special_terms_requested',
] as const;

type FunnelMetricKey = (typeof FUNNEL_KEYS)[number];

const FUNNEL_I18N_KEY: Record<
  FunnelMetricKey,
  | 'pages.admin.overview.funnel.submitted'
  | 'pages.admin.overview.funnel.inReview'
  | 'pages.admin.overview.funnel.accepted'
  | 'pages.admin.overview.funnel.declined'
  | 'pages.admin.overview.funnel.revoked'
  | 'pages.admin.overview.funnel.expired'
  | 'pages.admin.overview.funnel.specialTerms'
> = {
  accepted: 'pages.admin.overview.funnel.accepted',
  declined: 'pages.admin.overview.funnel.declined',
  expired: 'pages.admin.overview.funnel.expired',
  in_review: 'pages.admin.overview.funnel.inReview',
  revoked: 'pages.admin.overview.funnel.revoked',
  special_terms_requested: 'pages.admin.overview.funnel.specialTerms',
  submitted: 'pages.admin.overview.funnel.submitted',
};

export const PlatformOverview: FC = () => {
  const { t } = useTranslation();
  const funnelQuery = useDealFunnel();
  const dashboardQuery = useMyDashboard();

  if (
    (funnelQuery.isLoading && !funnelQuery.data) ||
    (dashboardQuery.isLoading && !dashboardQuery.data)
  ) {
    return <LoaderOverlay show />;
  }

  if (funnelQuery.isError && dashboardQuery.isError) {
    return (
      <Empty>
        <EmptyHeader>
          <EmptyTitle>{t('errors.overview.loadFailed')}</EmptyTitle>
          <EmptyDescription>{t('errors.unknown')}</EmptyDescription>
        </EmptyHeader>
      </Empty>
    );
  }

  const funnel = funnelQuery.data;
  const dashboard = dashboardQuery.data;
  const dealCounts = Object.entries(dashboard?.deals ?? {});

  return (
    <div className="space-y-6">
      <div className="grid gap-3 sm:grid-cols-2 lg:grid-cols-4">
        {FUNNEL_KEYS.map((key) => (
          <Card key={key}>
            <CardHeader className="pb-2">
              <CardTitle className="text-sm font-medium text-muted-foreground">
                {t(FUNNEL_I18N_KEY[key])}
              </CardTitle>
            </CardHeader>
            <CardContent>
              <p className="text-2xl font-semibold tabular-nums">
                {funnel?.[key] ?? 0}
              </p>
            </CardContent>
          </Card>
        ))}
      </div>

      <Card>
        <CardHeader>
          <CardTitle>{t('pages.admin.overview.conversion')}</CardTitle>
        </CardHeader>
        <CardContent>
          <p className="text-3xl font-semibold tabular-nums">
            {funnel?.conversion ?? '—'}
          </p>
        </CardContent>
      </Card>

      <div className="grid gap-3 sm:grid-cols-2">
        <Card>
          <CardHeader>
            <CardTitle>{t('pages.admin.overview.assignedToMe')}</CardTitle>
          </CardHeader>
          <CardContent>
            <p className="text-2xl font-semibold tabular-nums">
              {dashboard?.assigned_to_me ?? 0}
            </p>
          </CardContent>
        </Card>
        <Card>
          <CardHeader>
            <CardTitle>{t('pages.admin.overview.dealsByStatus')}</CardTitle>
          </CardHeader>
          <CardContent className="space-y-2">
            {dealCounts.length === 0 ? (
              <p className="text-sm text-muted-foreground">
                {t('pages.admin.overview.noDealStats')}
              </p>
            ) : (
              dealCounts.map(([status, count]) => (
                <div
                  key={status}
                  className="flex items-center justify-between text-sm">
                  <span className="text-muted-foreground capitalize">
                    {status.replaceAll('_', ' ')}
                  </span>
                  <span className="font-medium tabular-nums">{count}</span>
                </div>
              ))
            )}
          </CardContent>
        </Card>
      </div>
    </div>
  );
};

PlatformOverview.displayName = 'PlatformOverview';
